// Talk, Speaker, Cong
import { PublicTalk, Speaker } from "../../../../../types/scheduling/weekend";
import { useTranslation } from "react-i18next";
import { FilterByCallback, Option, TypeaheadPropsAndState } from "react-bootstrap-typeahead/types/types";
import { nameOfUser } from "../../../../../helpers/user";
import { NBSP } from "../../../../../helpers/util";
import { CollatorSingleton, containsFuzzy } from "../../../../../helpers/locale";
import { speakerCompare } from "../../../common";
import { Modal } from "react-bootstrap";
import { Search } from "react-bootstrap-icons";
import { Typeahead } from "react-bootstrap-typeahead";
import { useMemo } from "react";

type TSC = { pt?: PublicTalk; speaker: Speaker; label: string };
const speakerSearchCompare = (a: TSC, b: TSC) => {
  const aSpeaker = a.speaker;
  const bSpeaker = b.speaker;
  if (aSpeaker.congregation && bSpeaker.congregation && aSpeaker.congregation.id !== bSpeaker.congregation.id) {
    return CollatorSingleton.getInstance().compare(aSpeaker.congregation.name, bSpeaker.congregation.name);
  }
  if (aSpeaker.userId !== bSpeaker.userId) return speakerCompare(aSpeaker, bSpeaker);
  return (a.pt?.number ?? 0) - (b.pt?.number ?? 0);
};

const filterLogic: FilterByCallback = (option: Option, state: TypeaheadPropsAndState) => {
  const tsc = option as TSC;
  const filter = state.text.trim();
  if (!filter) return false; // prevent all options from showing

  if (containsFuzzy(nameOfUser(tsc.speaker), filter)) return true;
  const congName =
    tsc.speaker.congregation?.custom_name ?? tsc.speaker.congregation?.name ?? tsc.speaker.congregation_name;
  if (congName && containsFuzzy(congName, filter)) return true;
  return tsc.pt?.number === Number(filter);
};

export function SpeakerSearchModal(props: {
  show: boolean;
  setShow: (show: boolean) => void;
  speakers: Speaker[];
  setSelectedTalk: (set: number) => void;
  setSelectedSpeaker: (set: number) => void;
  setSelectedCong: (set: number) => void;
  setOverrideCong: (set: number) => void;
}) {
  const { t } = useTranslation();
  const handleClose = () => props.setShow(false);

  const handleClick = (speakerId: number, congId: number, talkNumber: number) => {
    props.setSelectedSpeaker(speakerId);
    props.setSelectedCong(congId);
    props.setOverrideCong(congId);
    props.setSelectedTalk(talkNumber);
    props.setShow(false);
  };

  const selectTSC = (selected: Option[]) => {
    const tsc = selected[0] as TSC;
    handleClick(tsc.speaker.id, tsc.speaker.congregation?.id || -1, tsc.pt?.number ?? 0);
  };

  const renderElements = (option: Option) => {
    const tsc = option as TSC;
    const ptNumber = tsc.pt?.number ? `#${tsc.pt.number}` : "";
    return (
      <span className="font-monospace">
        <span className="text-muted">{ptNumber.padStart(4, NBSP)}</span> |{" "}
        <span className="text-muted">{nameOfUser(tsc.speaker)}</span> |{" "}
        <span className="text-muted">
          {tsc.speaker.congregation?.custom_name ?? tsc.speaker.congregation?.name ?? tsc.speaker.congregation_name}
        </span>
      </span>
    );
  };

  const speakerOptions = useMemo(() => {
    return props.speakers
      .flatMap((s) => {
        if (s.public_talks.length === 0) {
          // return an entry without a public talk so that the speaker shows up in search
          return {
            speaker: s,
            label: "",
          };
        }
        return s.public_talks.map((pt) => {
          const tsc: TSC = {
            pt: pt,
            speaker: s,
            label: "", // not used, but must have
          };
          return tsc;
        });
      })
      .sort(speakerSearchCompare);
  }, [props.speakers]);

  return (
    <Modal show={props.show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>
          <Search className="me-3" />
          {t("search.hint")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Typeahead
          id="speaker_search"
          className={"font-monospace"}
          placeholder={[
            t("schedules.weekend.outlines"),
            t("schedules.weekend.speaker.0"),
            t("list.congregation.title"),
          ].join(" | ")}
          onChange={selectTSC}
          autoFocus
          minLength={1}
          emptyLabel={<></>}
          filterBy={filterLogic}
          renderMenuItemChildren={renderElements}
          options={speakerOptions}
        />
      </Modal.Body>
    </Modal>
  );
}
